import type { CatalogueCurrency } from '~/lib/catalogue-price'
import { currencyCaption } from '~/lib/currency-caption'
import { logNonFatal } from '~/lib/diagnostics'
import { validRemoteImageUrl } from '~/lib/remote-image'
import { api } from './api'
import {
  errorDetail,
  loadStarterPackRefetch,
  type ItemType,
  type ShopBootstrap,
  type ShopCollectionProduct,
  type ShopProduct,
} from './bootstrap'
import { localizeContentTitle, type CatalogueContent } from './catalogue'
import { compareByDisplayOrder } from './collection-order'
import {
  isRecord,
  readDisplayMetadata,
  type DisplayMetadata,
} from './display-metadata'
import { type HeroDisplay, parseHeroDisplay } from './main-products'
import { loadNativeIapPrice } from './native-iap-price'
import { platformSku } from './platform-sku'

// The starter pack (GLOSSARY.md "Starter pack (新手禮包)", ADR 0044): the
// shop-starter-pack collection's product. Membership in that collection is
// the only thing that makes a product a starter pack, and the server alone
// judges eligibility — an empty or missing collection means no starter pack.
// Derivation validates what bootstrap already holds (the entry, its item
// type, platform SKU, banner); hydration resolves what needs a further call
// (own purchase stats for ownership, the native IAP price).

// One content the pack grants, in item_contents order. Trees and land skins
// are catalogue identities (content, gid); currencies are an amount. A land
// skin or a currency carries the content's own icon when it has a usable one.
export type StarterPackContent =
  | {
      content: CatalogueContent
      gid: number
      iconUrl?: string
      kind: 'catalogue'
    }
  | {
      amount: number
      content: CatalogueCurrency
      iconUrl?: string
      kind: 'currency'
    }

export interface StarterPackCandidate {
  contents: StarterPackContent[]
  // The detail's slide images (GLOSSARY.md "Starter pack"), one per content in
  // content order, from display metadata's detail_img_urls. undefined = no
  // usable URL at that position: the slide shows blank.
  detailImageUrls: (string | undefined)[]
  display: HeroDisplay
  // Server-clock ms of the entry's per-user eligible_until — never the
  // product's global purchase_end_at. undefined = no deadline (missing or
  // null, reported with diagnostics).
  eligibleUntilMs?: number
  // The starter pack offer dialog's image (GLOSSARY.md), from display
  // metadata's offer_img_url. undefined = none usable: the dialog shows
  // without an image.
  offerImageUrl?: string
  product: ShopProduct
  sku: string
}

export interface StarterPack extends StarterPackCandidate {
  // Each content's name and quantity, in content order — the detail's slide
  // captions: a tree or land skin's native-catalog title ('' on a miss), a
  // currency's localized label × its amount. The reward reveal's captions.
  captions: string[]
  // The detail's slide captions, in slide order. Fixed copy for now rather
  // than derived from the contents (see DETAIL_CAPTIONS).
  detailCaptions: string[]
  // From the product's own purchase stats: total_count ≥ 1.
  owned: boolean
  // The Shop IAP price (GLOSSARY.md), native and storefront-localized.
  priceLocalized: string
  priceValue: number
}

// Bootstrap, or a post-purchase refetch: only the shop-starter-pack member
// and the item types are read.
export interface StarterPackSource {
  collections: Pick<ShopBootstrap['collections'], 'shop-starter-pack'>
  itemTypes: ItemType[]
}

type StarterPackOmissionReason =
  | 'invalid_collection'
  | 'invalid_collection_entry'
  | 'invalid_content'
  | 'invalid_display_metadata'
  | 'invalid_eligible_until'
  | 'invalid_purchase_type'
  | 'missing_banner_url'
  | 'missing_platform_sku'
  | 'missing_product'
  | 'native_price_unavailable'
  | 'purchase_stats_failed'

function reportOmission(
  reason: StarterPackOmissionReason,
  productId?: number,
  detail?: string,
): void {
  logNonFatal('shop_starter_pack_omitted', {
    reason,
    ...(productId === undefined ? {} : { product_id: String(productId) }),
    ...(detail === undefined ? {} : { detail }),
  })
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

// Allowed content types, kept in response order; any other type, or a value
// that is not a positive integer, omits the whole pack.
function deriveContents(
  itemType: ItemType | undefined,
): StarterPackContent[] | null {
  const raw = itemType?.item_contents
  if (!Array.isArray(raw) || raw.length === 0) return null
  const contents: StarterPackContent[] = []
  for (const content of raw as unknown[]) {
    if (!isRecord(content) || !isPositiveInteger(content.value)) return null
    const { type, value } = content
    if (type === 'tree_type') {
      contents.push({ content: type, gid: value, kind: 'catalogue' })
    } else if (type === 'land_skin') {
      const iconUrl = validRemoteImageUrl(content.icon_image_url)
      contents.push({
        content: type,
        gid: value,
        ...(iconUrl ? { iconUrl } : {}),
        kind: 'catalogue',
      })
    } else if (type === 'coin' || type === 'gem') {
      const iconUrl = content.icon_image_url
      contents.push({
        amount: value,
        content: type,
        kind: 'currency',
        ...(typeof iconUrl === 'string' && iconUrl.length > 0
          ? { iconUrl }
          : {}),
      })
    } else {
      return null
    }
  }
  return contents
}

// One image per content, matched by position. The images are not required:
// a position without a usable URL — the list absent, short, or holding
// something else there — is reported and its slide shows blank.
function deriveDetailImageUrls(
  displayMetadata: DisplayMetadata | null,
  contentCount: number,
  productId: number,
): (string | undefined)[] {
  const raw = displayMetadata?.detail_img_urls
  const list = Array.isArray(raw) ? raw : []
  return Array.from({ length: contentCount }, (_, index) => {
    const url = validRemoteImageUrl(list[index])
    if (url) return url
    logNonFatal('shop_invalid_detail_image_ignored', {
      index: String(index),
      product_id: String(productId),
    })
    return undefined
  })
}

// 'invalid' = a present but unparseable deadline: Shop could not honor the
// removal rule, so the pack is omitted with diagnostics.
function parseEligibleUntil(raw: unknown): number | undefined | 'invalid' {
  if (raw === undefined || raw === null) return undefined
  const ms = typeof raw === 'string' ? Date.parse(raw) : Number.NaN
  return Number.isFinite(ms) ? ms : 'invalid'
}

function deriveEntryCandidate(
  entry: ShopCollectionProduct,
  itemTypes: Map<number, ItemType>,
): StarterPackCandidate | null {
  const product = entry.item_type_product
  if (!isRecord(product) || typeof product.id !== 'number') {
    reportOmission('missing_product')
    return null
  }
  if (product.purchase_type !== 'iap') {
    reportOmission('invalid_purchase_type', product.id)
    return null
  }
  const contents = deriveContents(itemTypes.get(product.item_type_id ?? -1))
  if (!contents) {
    reportOmission('invalid_content', product.id)
    return null
  }
  const sku = platformSku(product as unknown as Record<string, unknown>)
  if (!sku) {
    reportOmission('missing_platform_sku', product.id)
    return null
  }
  const displayMetadata = readDisplayMetadata(product.display_metadata)
  const heroDisplay = parseHeroDisplay(displayMetadata, {
    productId: product.id,
  })
  if ('failure' in heroDisplay) {
    reportOmission(heroDisplay.failure, product.id)
    return null
  }
  const eligibleUntilMs = parseEligibleUntil(entry.eligible_until)
  if (eligibleUntilMs === 'invalid') {
    reportOmission('invalid_eligible_until', product.id)
    return null
  }
  // The server is expected to send a per-user deadline: without one the pack
  // shows with no countdown, and the gap is reported. Nothing after this can
  // omit the entry, so it reports once per derivation.
  if (eligibleUntilMs === undefined) {
    logNonFatal('shop_starter_pack_missing_eligible_until', {
      product_id: String(product.id),
    })
  }
  const offerImageUrl = validRemoteImageUrl(displayMetadata?.offer_img_url)
  return {
    contents,
    detailImageUrls: deriveDetailImageUrls(
      displayMetadata,
      contents.length,
      product.id,
    ),
    display: heroDisplay.display,
    eligibleUntilMs,
    ...(offerImageUrl ? { offerImageUrl } : {}),
    product,
    sku,
  }
}

// At most one candidate: the collection is expected to hold at most one
// product, and Shop renders only the first valid one in display order.
// undefined = no starter pack — the collection missing, empty, or holding
// nothing valid.
export function deriveStarterPackCandidate(
  source: StarterPackSource,
): StarterPackCandidate | undefined {
  const collection = source.collections['shop-starter-pack']
  if (collection === undefined) return undefined
  const entries: unknown = isRecord(collection)
    ? collection.item_type_product_collection_products
    : undefined
  if (!Array.isArray(entries)) {
    reportOmission('invalid_collection')
    return undefined
  }
  if (entries.length === 0) return undefined

  const itemTypes = new Map<number, ItemType>()
  for (const itemType of source.itemTypes) {
    if (isRecord(itemType)) itemTypes.set(itemType.id, itemType)
  }
  const valid = (entries as unknown[]).filter(
    (entry): entry is ShopCollectionProduct => {
      if (isRecord(entry) && typeof entry.id === 'number') return true
      reportOmission('invalid_collection_entry')
      return false
    },
  )
  for (const entry of valid.sort(compareByDisplayOrder)) {
    const candidate = deriveEntryCandidate(entry, itemTypes)
    if (candidate) return candidate
  }
  return undefined
}

interface OwnPurchaseStatsResponse {
  stats?: { total_count?: unknown }
}

async function loadOwned(userId: number, productId: number): Promise<boolean> {
  const response = await api.service
    .get(`store/users/${userId}/product/${productId}/own-purchase-stats`)
    .json<OwnPurchaseStatsResponse>()
  const count = response?.stats?.total_count
  if (typeof count !== 'number' || !Number.isFinite(count)) {
    throw new Error('invalid total_count')
  }
  return count >= 1
}

// A content's caption: a tree or land skin names itself by its native-catalog
// title; a currency is its localized label × the amount ("Time Crystals ×
// 30"). Never fails — a title lookup that misses or fails is ''.
function contentCaption(
  content: StarterPackContent,
  productId: number,
): Promise<string> {
  if (content.kind === 'catalogue') {
    return localizeContentTitle(content.content, content.gid, {
      product_id: productId,
    })
  }
  return Promise.resolve(currencyCaption(content.content, content.amount))
}

// The detail's slide captions, fixed rather than derived from the pack's
// contents until the server can say what each slide reads: the tree's
// native-catalog title, then the two currency grants. A slide past the end
// reads blank.
const DETAIL_CAPTIONS: (
  | { content: 'tree_type'; gid: number }
  | { amount: number; content: CatalogueCurrency }
)[] = [
  { content: 'tree_type', gid: 77 },
  { amount: 30, content: 'gem' },
  { amount: 600, content: 'coin' },
]

function detailCaptions(productId: number): Promise<string[]> {
  return Promise.all(
    DETAIL_CAPTIONS.map((caption) =>
      'gid' in caption
        ? localizeContentTitle(caption.content, caption.gid, {
            product_id: productId,
          })
        : Promise.resolve(currencyCaption(caption.content, caption.amount)),
    ),
  )
}

type StarterPackResolution =
  | { pack: StarterPack }
  | {
      detail: string
      failure: 'native_price_unavailable' | 'purchase_stats_failed'
    }

// Ownership, the native price and the captions, in parallel. Total.
async function resolveStarterPack(
  candidate: StarterPackCandidate,
  userId: number,
): Promise<StarterPackResolution> {
  const productId = candidate.product.id
  // Captions never fail, so they resolve beside the verdicts.
  const captions = Promise.all(
    candidate.contents.map((content) => contentCaption(content, productId)),
  )
  const fixedDetailCaptions = detailCaptions(productId)
  const [owned, price] = await Promise.allSettled([
    loadOwned(userId, productId),
    loadNativeIapPrice({ productId: candidate.product.id, sku: candidate.sku }),
  ])
  if (owned.status === 'rejected') {
    return {
      detail: errorDetail(owned.reason),
      failure: 'purchase_stats_failed',
    }
  }
  if (price.status === 'rejected') {
    return {
      detail: errorDetail(price.reason),
      failure: 'native_price_unavailable',
    }
  }
  return {
    pack: {
      ...candidate,
      captions: await captions,
      detailCaptions: await fixedDetailCaptions,
      owned: owned.value,
      ...price.value,
    },
  }
}

// Bootstrap hydration. Total: a failed stats lookup hides the pack rather
// than offering a purchase the user may already have made (ADR 0044), and an
// unresolvable price omits it like any hydrated Shop product — both resolve
// to null with diagnostics.
export async function hydrateStarterPack(
  candidate: StarterPackCandidate,
  userId: number,
): Promise<StarterPack | null> {
  const resolution = await resolveStarterPack(candidate, userId)
  if ('pack' in resolution) return resolution.pack
  reportOmission(resolution.failure, candidate.product.id, resolution.detail)
  return null
}

export interface StarterPackRefetchResult {
  // The item types the refetch fetched, for the caller to hold from now on.
  itemTypes: ItemType[]
  // undefined = no starter pack any more: the collection came back missing,
  // empty, or holding nothing valid.
  pack?: StarterPack
}

// The refetch after a committed purchase (GLOSSARY.md "Starter pack
// (新手禮包)"): the collection, its missing item types, then the pack's
// purchase stats and native price. Total: any failed step records
// diagnostics and resolves to null — the caller keeps its current state
// until the next purchase. A pack the server no longer returns, or returns
// malformed (reported as an omission at derivation), resolves to no pack.
export async function refetchStarterPack(
  userId: number,
  knownItemTypes: ItemType[],
): Promise<StarterPackRefetchResult | null> {
  const fail = (step: string, detail: string, productId?: number) => {
    logNonFatal('shop_starter_pack_refetch_failed', {
      step,
      detail,
      ...(productId === undefined ? {} : { product_id: String(productId) }),
    })
    return null
  }
  let loaded: Awaited<ReturnType<typeof loadStarterPackRefetch>>
  try {
    loaded = await loadStarterPackRefetch(
      userId,
      new Set(knownItemTypes.map((itemType) => itemType.id)),
    )
  } catch (error) {
    return fail('collection', errorDetail(error))
  }
  const candidate = deriveStarterPackCandidate({
    collections: { 'shop-starter-pack': loaded.collection },
    itemTypes: [...knownItemTypes, ...loaded.itemTypes],
  })
  if (!candidate) return { itemTypes: loaded.itemTypes }
  const resolution = await resolveStarterPack(candidate, userId)
  if ('failure' in resolution) {
    return fail(resolution.failure, resolution.detail, candidate.product.id)
  }
  return { itemTypes: loaded.itemTypes, pack: resolution.pack }
}
